import React, { useEffect } from "react";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import { Icon, LatLngTuple } from "leaflet";
import "leaflet/dist/leaflet.css";
import { EnvironmentOutlined } from "@ant-design/icons";
import pinImage from "../../../assets/pin.png";
import styles from "./RegisterOrderForm.module.css";

const PIN_ICON = new Icon({ iconUrl: pinImage, iconSize: [38, 38] });
const STREET_ZOOM = 15;

/**
 * Keeps the view on the pin whenever the address changes.
 *
 * Declared at module level on purpose. It used to be defined inside the register page, which made
 * it a new component type on every render — so it remounted, and re-centred the map, on every
 * keystroke anywhere in the form.
 */
const FollowPosition = ({ position }: { position: LatLngTuple }) => {
	const map = useMap();
	useEffect(() => {
		map.setView(position, STREET_ZOOM, { animate: true });
	}, [map, position]);
	return null;
};

type DeliveryMapProps = {
	/** Null until an address with coordinates is chosen; a placeholder line is shown instead. */
	position: LatLngTuple | null;
	placeholder?: string;
};

/** Where the order will be delivered — confirmation for the street picked above it. */
const DeliveryMap = ({ position, placeholder }: DeliveryMapProps) => {
	if (!position) {
		return (
			<div className={styles.mapPlaceholder}>
				<EnvironmentOutlined className={styles.mapPlaceholderIcon} />
				{placeholder}
			</div>
		);
	}

	return (
		<div className={styles.map}>
			<MapContainer
				center={position}
				zoom={STREET_ZOOM}
				scrollWheelZoom={false}
				style={{ width: "100%", height: "100%" }}
			>
				<TileLayer
					attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
				/>
				<Marker position={position} icon={PIN_ICON} />
				<FollowPosition position={position} />
			</MapContainer>
		</div>
	);
};

export default DeliveryMap;
